<template>
  <v-container fluid>
    <v-card class="mx-auto shared-pdf-card">
      <v-card-title>Geteilte PDF-Transaktion</v-card-title>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <div v-if="isLoading" class="py-6 text-center">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else>
          <div v-if="sharedFile" class="text-caption text-medium-emphasis mb-4">
            {{ sharedFile.name || 'geteilte PDF' }} -
            {{ formatFileSize(sharedFile.size) }}
          </div>

          <PdfTransactionImportDialog
            v-if="sharedFile"
            mode="inline"
            :file="sharedFile"
            auto-analyze
            @imported="handlePdfImported"
            @error="handlePdfError"
          >
            <template #inline-actions-start>
              <v-btn
                class="shared-pdf-action-btn"
                variant="text"
                @click="$router.push({ name: 'Stocks' })"
              >
                Zu Stocks
              </v-btn>
            </template>
          </PdfTransactionImportDialog>
        </template>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import PdfTransactionImportDialog from '@/components/PdfTransactionImportDialog.vue'
import { isPdfFile, normalizePdfFile } from '@/utils/pdfFile'

const SHARE_DB_NAME = 'rateit-shared-images'
const SHARE_DB_VERSION = 1
const SHARE_STORE_NAME = 'incoming'

function openShareDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(SHARE_DB_NAME, SHARE_DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(SHARE_STORE_NAME)) {
        db.createObjectStore(SHARE_STORE_NAME, { keyPath: 'id' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function getSharedRecord(shareId) {
  const db = await openShareDb()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(SHARE_STORE_NAME, 'readonly')
    const request = transaction.objectStore(SHARE_STORE_NAME).get(shareId)

    request.onsuccess = () => resolve(request.result || null)
    request.onerror = () => reject(request.error)
  })
}

async function deleteSharedRecord(shareId) {
  const db = await openShareDb()

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(SHARE_STORE_NAME, 'readwrite')
    transaction.objectStore(SHARE_STORE_NAME).delete(shareId)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

export default {
  name: 'SharedTransactionPdfView',

  components: {
    PdfTransactionImportDialog
  },

  data() {
    return {
      isLoading: true,
      error: '',
      sharedRecord: null,
      sharedFile: null
    }
  },

  async mounted() {
    await this.loadSharedPdf()
  },

  methods: {
    async loadSharedPdf() {
      this.isLoading = true
      this.error = ''

      try {
        await this.$store.dispatch('viewMe')
        await this.$store.dispatch('getStocks')

        const shareId = String(this.$route.query.shareId || '').trim()
        if (!shareId) {
          this.error = 'Keine geteilte PDF gefunden.'
          return
        }

        const record = await getSharedRecord(shareId)
        let file = null
        for (const candidate of record?.files || []) {
          if (await isPdfFile(candidate)) { file = candidate; break }
        }

        if (!file) {
          this.error = 'Die geteilte Datei ist keine PDF.'
          return
        }

        this.sharedRecord = record
        this.sharedFile = await normalizePdfFile(file)
      } catch (error) {
        console.error(error)
        this.error = 'Die geteilte PDF konnte nicht geladen werden.'
      } finally {
        this.isLoading = false
      }
    },

    formatFileSize(size) {
      const numberValue = Number(size)
      if (!Number.isFinite(numberValue)) return ''

      return `${(numberValue / 1024 / 1024).toFixed(2)} MB`
    },

    async handlePdfImported() {
      if (!this.sharedRecord?.id) return

      try {
        await deleteSharedRecord(this.sharedRecord.id)
      } catch (error) {
        console.error(error)
      }
    },

    handlePdfError(error) {
      console.error(error)
    }
  }
}
</script>

<style scoped>
.shared-pdf-card {
  max-width: 860px;
}

.shared-pdf-action-btn {
  min-width: 0;
}
</style>
